// Orchestrates a full claude -p session. Hard order (test 45 spies on this):
// preflight -> t0 = deps.now() -> bind `{{WORKTREE}}` rules (06g) ->
// writeBinding(t0) -> build argv (contract_error before spawn if allowedTools
// missing) -> spawn -> stdin prompt then close -> stream -> deadline/KILL if
// needed -> wait groupReaped before returning.
// NO hardcoded operational numbers here — everything flows from RuntimeLimits
// (test 44's grep gate; the poll interval literal lives in session-runner-exit-waiter.ts).
import {
  type BindingWriteResult,
  type BindingWriterInput,
  writeAutopilotBindingFile,
} from "./autopilot-binding-file-writer.js";
import { assertNoAnthropicEnv } from "./child-env-allowlist-builder.js";
import { type SpawnFn, spawnClaudeProcessGroup } from "./claude-process-group-spawner.js";
import { deadlineReasonAndCategory } from "./deadline-rate-limit-signal.js";
import type { classifyAttemptError } from "./error-classifier.js";
import type { RuntimeLimits } from "./runtime-limits-from-effective-config.js";
import { runSessionPreflightChecks } from "./session-preflight-checks.js";
import { buildArgvAndEnv } from "./session-runner-argv-env-builder.js";
import { waitForExitOrDeadline } from "./session-runner-exit-waiter.js";
import { attachStreamConsumers, finalizeResult } from "./session-runner-stream-consumer.js";
import type { SystemInitInventory } from "./stream-json-event-parser.js";
import { bindToolRulesToWorktree } from "./worktree-bound-tool-rules.js";

export interface SessionSpec {
  missionId: string;
  ticketId: string;
  runId: string;
  sessionId: string;
  kind: string;
  missionWorkspace: string;
  packRoot: string;
  repoRoot: string;
  prompt: string;
  allowedTools: string[];
  allowedPaths: string[];
  workerModel?: string;
  configEnvOverlay?: Record<string, string | undefined>;
  // Known secret values to redact from stdout/stderr before they reach any
  // sink (ring buffer, evidence, classifier input). Defaults to [] when
  // absent -- analogous to allowedTools/allowedPaths, mission-specific
  // config the caller supplies (review fix 3).
  secretValues?: string[];
}

export interface SessionRunnerDeps {
  spawn: SpawnFn;
  now: () => number;
  sleep: (ms: number) => Promise<void>;
  writeBinding?: typeof writeAutopilotBindingFile;
}

export interface SessionRunResult {
  outcome: "ok" | "contract_error" | "startup_failed" | "deadline_exceeded" | "blocked";
  reason?: string;
  category?: ReturnType<typeof classifyAttemptError>["category"];
  result?: unknown;
  bindingPath?: string;
  bindingHash?: string;
  spawnAt: number;
  spawnedAt?: number;
  initInventory?: SystemInitInventory;
  groupReaped?: boolean;
  // P13 Agent A: observability for a `no_result_json_found`/
  // `invalid_structured_output_schema` contract_error — see
  // terminal-result-extractor.ts's TerminalResultContractError for the
  // redaction/scope contract (names only for schemaErrorPaths, tail-capped
  // raw text for rawResultTail). Both undefined for every other outcome.
  rawResultTail?: string;
  schemaErrorPaths?: string[];
  // P13 Agent B: usage/cost figures read straight off the worker's own
  // result event (session-runner-stream-consumer.ts's extractUsage) —
  // undefined when no single result event was ever seen (startup/deadline
  // failures), per-field undefined when the worker reported the event but
  // omitted that particular figure. Fed into
  // mission-cost-accounting.ts's recordAttemptUsage by the caller.
  usage?: import("./session-runner-usage-extraction.js").SessionUsage;
  // Phase 04 (F1b, R-23): worker's stderr tail (already redacted by
  // session-runner-stream-consumer.ts's SecretRedactor), only populated when
  // `outcome !== "ok"` — surfaced so `attempt_finished.reasons` can show the
  // REAL reason a session failed instead of just "missing_result_event".
  stderrTail?: string;
}

function contractErrorResult(spawnAt: number, reason: string): SessionRunResult {
  return { outcome: "contract_error", reason, spawnAt };
}

export async function runClaudeSession(
  spec: SessionSpec,
  limits: RuntimeLimits,
  deps: SessionRunnerDeps,
): Promise<SessionRunResult> {
  const preflight = await runSessionPreflightChecks(spec.missionWorkspace, spec.packRoot);
  if (!preflight.ok) {
    return { outcome: "blocked", reason: preflight.reason, spawnAt: deps.now() };
  }

  const promptBytes = Buffer.byteLength(spec.prompt, "utf8");
  if (promptBytes > limits.limits.promptMaxKib * 1024) {
    return contractErrorResult(deps.now(), "prompt_over_max_kib");
  }

  if (spec.configEnvOverlay) {
    try {
      assertNoAnthropicEnv(spec.configEnvOverlay);
    } catch {
      return contractErrorResult(deps.now(), "anthropic_env_present_in_config");
    }
  }

  // t0 = spawn_at: single moment for deadline/startup/expires_at math.
  // Called BEFORE writeBinding (test 45 asserts this ordering via spy).
  const t0 = deps.now();

  // 06g R1b: bind `{{WORKTREE}}` ONCE — binding file and argv carry the same
  // literal rules; a leftover placeholder is a contract_error before spawn.
  let allowedTools: string[];
  try {
    allowedTools = bindToolRulesToWorktree(spec.allowedTools, { repoRoot: spec.repoRoot });
  } catch (err) {
    return contractErrorResult(t0, `unbound_tool_rule:${(err as Error).message}`);
  }
  const bound: SessionSpec = { ...spec, allowedTools };

  const bindingInput: BindingWriterInput = {
    missionId: spec.missionId,
    ticketId: spec.ticketId,
    runId: spec.runId,
    sessionId: spec.sessionId,
    repoRoot: spec.repoRoot,
    allowedPaths: spec.allowedPaths,
    allowedTools,
    kind: spec.kind,
  };
  const writeBinding = deps.writeBinding ?? writeAutopilotBindingFile;
  let binding: BindingWriteResult;
  try {
    binding = await writeBinding(spec.missionWorkspace, bindingInput, t0, limits);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return contractErrorResult(t0, `binding_write_failed:${msg}`);
  }

  const built = buildArgvAndEnv(bound, t0, binding);
  if (!built.ok) return built.error;
  const { argv, env } = built;

  const spawned = await spawnClaudeProcessGroup(
    "claude",
    argv,
    { cwd: spec.missionWorkspace, env },
    { spawn: deps.spawn, sleep: deps.sleep },
  );
  if (!spawned.ok) {
    return {
      outcome: "startup_failed",
      reason: `spawn_failed:${spawned.code}`,
      spawnAt: t0,
      bindingPath: binding.bindingPath,
      bindingHash: binding.checksum,
    };
  }
  const group = spawned;
  const spawnedAt = deps.now();

  group.child.stdin?.on("error", () => {}); // EPIPE khi worker bị kill trước khi đọc hết prompt
  group.child.stdin?.write(spec.prompt);
  group.child.stdin?.end();

  const stream = attachStreamConsumers(group.child, limits, spec.secretValues ?? [], deps.now);

  const exitInfo = await waitForExitOrDeadline(group.child, deps, t0, limits, () => stream.sawInit);

  // Unified cleanup point: group.kill() runs exactly once here whichever
  // branch waitForExitOrDeadline took; idempotent on natural exit (ESRCH is
  // caught in the spawner) and the one place that VERIFIES groupReaped.
  const kill = await group.kill(limits.graceMs);

  if (exitInfo.kind === "startup_timeout" || exitInfo.kind === "deadline") {
    const isStartup = exitInfo.kind === "startup_timeout";
    return {
      outcome: isStartup ? "startup_failed" : "deadline_exceeded",
      // C1-F4: a deadline hit while still waiting on a rate-limit retry ⇒ quota-sleep.
      ...(isStartup
        ? { reason: "no_init_event_before_startup_timeout" }
        : deadlineReasonAndCategory(stream, deps.now())),
      spawnAt: t0,
      spawnedAt,
      bindingPath: binding.bindingPath,
      bindingHash: binding.checksum,
      groupReaped: kill.groupReaped,
      initInventory: stream.initInventory,
    };
  }

  return finalizeResult(bound, t0, spawnedAt, binding, stream, exitInfo, kill.groupReaped);
}
